import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  showToastifySuccess,
  showToastifyError,
} from "../../common/tostify/Toastifyresponse";
import {
  createRoleSlice,
  updateRoleSlice,
  getRoleSlice,
  getRoleById,
  deleteRoleSlice,
  resetCreateMessages,
  resetDeleteMessages,
  resetUpdateMessages,
} from "../../../store/slices/roleSlice";
import { Modal } from "react-bootstrap";
import Table from "../../common/Table/table";
import Addrolemodal from "./addrolemodal";
import { useNavigate } from "react-router-dom";
import ViewModal from "../../common/viewModal/globalViewModal";
import ShowTostifyMessageAndReset from "../../common/showTostifyMessageAndReset/showTostifyMessageAndReset";
import { getApplication } from "../../../store/slices/application";

const Role = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { clinicInfo } = useSelector((state) => state.clinic);
  const { token } = useSelector((state) => state.auth);
  const { application } = useSelector((state) => state.application);
  const { tableStatus } = useSelector((state) => state.tableStatusChange);

  const {
    roles,
    roleInFo,
    createSuccessMessage,
    createErrorMessage,
    updateSuccessMessage,
    updateErrorMessage,
    deleteSuccessMessage,
    deleteErrorMessage,
    totalPages,
    totalCount,
    hasMore,
    limit,
    currentPage,
    loading,
    searchMessage,
  } = useSelector((state) => state.role);

  // ✅ SINGLE SOURCE OF TRUTH
  const clinicId = clinicInfo?.[0]?.clinicId || null;

  const showTostifyMessageAndReset = ShowTostifyMessageAndReset();

  const [modalAction, setModalAction] = useState("create");
  const [showModal, setModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showDeleteModal, setDeleteModalVisible] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [showGlobalViewModal, setShowGlobalViewModal] = useState(false);
  const [showStatus, setShowStatus] = useState(null);
  const [pageLimit, setPageLimit] = useState(10);

  const [formData, setFormData] = useState({
    id: "",
    customRoleId: "",
    name: "",
    description: "",
    status: "Active",
    clinicId: "",
    application: "",
    applicationCustomId: 2,
  });

  const [inputError, setInputError] = useState({
    text: false,
    onlyNumber: false,
  });

  /* ---------------- TOASTS ---------------- */
  useEffect(() => {
    showTostifyMessageAndReset(
      createSuccessMessage,
      showToastifySuccess,
      resetCreateMessages
    );
    showTostifyMessageAndReset(
      createErrorMessage,
      showToastifyError,
      resetCreateMessages
    );
    showTostifyMessageAndReset(
      updateSuccessMessage,
      showToastifySuccess,
      resetUpdateMessages
    );
    showTostifyMessageAndReset(
      updateErrorMessage,
      showToastifyError,
      resetUpdateMessages
    );
    showTostifyMessageAndReset(
      deleteSuccessMessage,
      showToastifySuccess,
      resetDeleteMessages
    );
    showTostifyMessageAndReset(
      deleteErrorMessage,
      showToastifyError,
      resetDeleteMessages
    );
  }, [
    createSuccessMessage,
    createErrorMessage,
    updateSuccessMessage,
    updateErrorMessage,
    deleteSuccessMessage,
    deleteErrorMessage,
    showTostifyMessageAndReset,
  ]);

  /* ---------------- APPLICATION ---------------- */
  useEffect(() => {
    if (token) {
      dispatch(getApplication({ token, customId: 2 }));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (application?.length) {
      setFormData((prev) => ({
        ...prev,
        application: application[0]?._id,
      }));
    }
  }, [application]);

  /* ---------------- CLINIC ID ---------------- */
  useEffect(() => {
    if (clinicId) {
      setFormData((prev) => ({
        ...prev,
        clinicId,
      }));
    }
  }, [clinicId]);

  /* ---------------- FETCH ROLES ---------------- */
  const fetchRole = useCallback(
    (page = 1, lim = pageLimit, search = searchValue) => {
      if (!clinicId || !token) return;

      dispatch(
        getRoleSlice({
          clinicId,
          status: tableStatus,
          applicationCustomId: 2,
          token,
          currentPage: page,
          limit: lim,
          role: search || undefined,
        })
      );
    },
    [clinicId, token, dispatch, tableStatus, searchValue, pageLimit]
  );

  useEffect(() => {
    fetchRole();
  }, [fetchRole, showModal, showDeleteModal, showGlobalViewModal]);

  const handlePageChange = (page) => {
    fetchRole(page, pageLimit, searchValue);
  };

  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    fetchRole(1, newLimit, searchValue);
  };

  /* ---------------- HANDLERS ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermission = (role) => {
    sessionStorage.setItem("roleName", role.name);
    sessionStorage.setItem("roleId", role.id);
    navigate("/Setmodulerolepermission");
  };

  const handleAddOrUpdateRole = async (e) => {
    e.preventDefault();
    const { text, onlyNumber } = inputError;
    if (text || onlyNumber || !clinicId) return;

    if (modalAction === "create") {
      await dispatch(
        createRoleSlice({
          ...formData,
          clinicId,
          application: application?.[0]?._id,
          token,
        })
      );
    } else {
      await dispatch(
        updateRoleSlice({
          id: formData?.id,
          customRoleId: formData?.customRoleId,
          name: formData?.name,
          description: formData?.description,
          status: formData?.status,
          clinicId,
          application: application?.[0]?._id,
          token,
        })
      );
    }

    fetchRole();
    setModalVisible(false);
    setFormData({
      id: "",
      customRoleId: "",
      name: "",
      description: "",
      status: "Active",
      clinicId,
      application: application?.[0]?._id,
      applicationCustomId: 2,
    });
  };

  const handleEdit = (role) => {
    setModalAction("edit");
    setModalVisible(true);
    setFormData({
      id: role?.id,
      customRoleId: role?.customRoleId,
      name: role?.name,
      description: role?.description,
      status: role?.status,
      clinicId,
      application: application?.[0]?._id,
      applicationCustomId: 2,
    });
  };

  const handleDelete = (role) => {
    setRoleToDelete(role);
    setDeleteModalVisible(true);
  };

  const handleDeleteRole = async () => {
    await dispatch(deleteRoleSlice({ id: roleToDelete.id, token }));
    setDeleteModalVisible(false);
  };

  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchValue(value);
      fetchRole(1, limit, value);
    },
    [fetchRole, limit]
  );

  const handleShowGlobalViewModal = async (data) => {
    await dispatch(getRoleById({ id: data.id, token }));
    setShowGlobalViewModal(true);
  };

  const handleUpdateStatus = async (status, item) => {
    await dispatch(
      updateRoleSlice({
        id: item?.id,
        status,
        clinicId,
        token,
      })
    );
    fetchRole();
  };

  /* ---------------- TABLE ---------------- */
  const tableHead = [
    { title: "Name", key: "name" },
    { title: "RoleId", key: "customRoleId" },
    { title: "Description", key: "description" },
    { title: "Status", key: "status" },
  ];

  const titel = [
    { title: "Name", key: "name" },
    { title: "Description", key: "description" },
    { title: "Status", key: "status" },
  ];

  return (
    <>
      <Table
        tableHead={tableHead}
        tableBody={roles?.map((item) => ({
          id: item?._id,
          name: item?.name,
          customRoleId: item?.customRoleId,
          description: item?.description,
          status: item?.status,
        }))}
        handleAddBtn
        addModal={() => {
          setModalAction("create");
          setModalVisible(true);
        }}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        handleSearch={handleSearch}
        handlePermission={handlePermission}
        showExtraicons
        handleShowGlobalViewModal={handleShowGlobalViewModal}
        showStatus={showStatus}
        setShowStatus={setShowStatus}
        handleUpdateStatus={handleUpdateStatus}
        // fetchMoreData={fetchMoreData}
        // hasMore={hasMore}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        limit={pageLimit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        showPagination={true}
        showLimitDropdown={true}
        errorMessage={searchMessage}
        searchValue={searchValue}
        isLoading={loading}
      />

      <Addrolemodal
        handleAddOrUpdateRole={handleAddOrUpdateRole}
        setModalVisible={setModalVisible}
        modalAction={modalAction}
        formData={formData}
        showModal={showModal}
        handleChange={handleChange}
        setInputError={setInputError}
      />

      <Modal
        centered
        show={showDeleteModal}
        onHide={() => setDeleteModalVisible(false)}
      >
        <div className="modal-header">
          <h5 className="modal-title">Delete Role</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModalVisible(false)}
          ></button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to delete this role?</p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModalVisible(false)}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDeleteRole}
          >
            Delete
          </button>
        </div>
      </Modal>

      <ViewModal
        showGlobalViewModal={showGlobalViewModal}
        setShowGlobalViewModal={setShowGlobalViewModal}
        modalSize="lg"
        titel={titel}
        content={{
          name: roleInFo?.name,
          description: roleInFo?.description,
          status: roleInFo?.status,
        }}
      />
    </>
  );
};

export default Role;
